import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { InstitutionalHolderDetailPanel } from '@/utils/institutionalHolderQuarterHistory';
import {
  formatHolderHistoryShares,
  quarterKeyToLabel,
} from '@/utils/institutionalHolderQuarterHistory';

const TOOLTIP_WIDTH_PX = 280;
const VIEWPORT_PAD = 8;

function clampTooltipPosition(anchor: DOMRect, height: number): { left: number; top: number } {
  let left = anchor.left + anchor.width / 2 - TOOLTIP_WIDTH_PX / 2;
  let top = anchor.bottom + VIEWPORT_PAD;
  left = Math.max(
    VIEWPORT_PAD,
    Math.min(left, window.innerWidth - TOOLTIP_WIDTH_PX - VIEWPORT_PAD)
  );
  if (top + height > window.innerHeight - VIEWPORT_PAD) {
    top = anchor.top - height - VIEWPORT_PAD;
  }
  top = Math.max(VIEWPORT_PAD, top);
  return { left, top };
}

function formatReportedValueUsd(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
}

function formatFirstFiledLabel(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})/);
  if (!m) return iso;
  const month = parseInt(m[2]!, 10);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[month - 1] ?? m[2]} ${m[1]}`;
}

function HolderSummaryStats({ detail }: { detail: InstitutionalHolderDetailPanel }) {
  const stats: { value: string; label: string }[] = [];

  if (detail.ownership != null && Number.isFinite(detail.ownership)) {
    stats.push({
      value: `${detail.ownership.toFixed(2)}%`,
      label: 'Ownership',
    });
  }
  if (detail.marketValue != null && detail.marketValue > 0) {
    stats.push({
      value: formatReportedValueUsd(detail.marketValue),
      label: 'Reported value',
    });
  }
  if (detail.firstAdded) {
    stats.push({
      value: formatFirstFiledLabel(detail.firstAdded),
      label: 'First filed',
    });
  }

  if (stats.length === 0) return null;

  const gridCols =
    stats.length >= 3 ? 'grid-cols-3' : stats.length === 2 ? 'grid-cols-2' : 'grid-cols-1';

  return (
    <div
      className={`mt-2.5 grid ${gridCols} gap-px overflow-hidden rounded-lg border border-slate-100 bg-slate-100`}
    >
      {stats.map((stat) => (
        <div key={stat.label} className="bg-slate-50/90 px-2 py-2 text-center">
          <p className="text-xs font-semibold tabular-nums text-slate-900 leading-none">
            {stat.value}
          </p>
          <p className="mt-1 text-[10px] font-medium text-slate-500 leading-none">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}

export function InstitutionalHolderChartTooltip({
  detail,
  anchorRect,
  onClose,
}: {
  detail: InstitutionalHolderDetailPanel;
  anchorRect: DOMRect;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>(() =>
    clampTooltipPosition(anchorRect, 200)
  );

  useLayoutEffect(() => {
    const el = ref.current;
    const h = el?.offsetHeight ?? 200;
    setPosition(clampTooltipPosition(anchorRect, h));
  }, [anchorRect, detail]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (t instanceof Element && t.closest('[data-inst-overlay="quarter-list"]')) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const cikLabel = detail.cik ? `CIK ${detail.cik.replace(/^0+/, '')}` : 'CIK unknown';

  return createPortal(
    <div
      ref={ref}
      data-inst-overlay="holder-detail"
      role="dialog"
      aria-label={`${detail.investorName} institutional holdings`}
      className="fixed z-[61] rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-lg"
      style={{ left: position.left, top: position.top, width: TOOLTIP_WIDTH_PX }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-slate-900 leading-snug tracking-tight">
            {detail.institutionName}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {detail.positionTypeLabel ? (
              <span className="inline-flex rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                {detail.positionTypeLabel}
              </span>
            ) : null}
            <span className="text-[11px] text-slate-500 tabular-nums">
              {cikLabel}
              <span className="mx-1 text-slate-300">·</span>
              {detail.symbol}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <HolderSummaryStats detail={detail} />

      {detail.summaryQuarterKey !== detail.highlightedQuarterKey ? (
        <p className="mt-2 text-[10px] leading-snug text-slate-400">
          Summary from {quarterKeyToLabel(detail.summaryQuarterKey)} filing
          {detail.highlightedQuarterKey !== detail.summaryQuarterKey
            ? ` · bold row is ${quarterKeyToLabel(detail.highlightedQuarterKey)}`
            : ''}
        </p>
      ) : null}

      <div className="mt-3 border-t border-slate-100 pt-2.5">
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400 mb-1.5">
          Shares held ({detail.symbol})
        </p>
        <ul className="space-y-1">
          {detail.rows.map((row) => (
            <li
              key={row.quarterKey}
              className={`flex items-baseline justify-between gap-2 text-xs ${
                row.isHighlighted ? 'font-semibold text-slate-900' : 'font-normal text-slate-500'
              }`}
            >
              <span>{row.quarterLabel}</span>
              <span className="tabular-nums text-right shrink-0">
                {formatHolderHistoryShares(row.shares, row.isSoldOut)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body
  );
}
